/**
 * @module config/form/pages/hospitalization-date
 * @description Standard form system configuration for Hospitalization Date page
 * VA Form 21-2680 - Examination for Housebound Status or Permanent Need for Regular Aid and Attendance
 */

import {
  currentOrPastDateUI,
  currentOrPastDateSchema,
} from 'platform/forms-system/src/js/web-component-patterns';
import { isValidDateRange } from 'platform/forms-system/src/js/utilities/validations';
import { convertToDateField } from 'platform/forms-system/src/js/validation';
import { getHospitalizationDateTitle, isClaimantVeteran } from '../utils';

const isDateAfterDOB = (errors, fieldData, formData) => {
  const dateToValidate = convertToDateField(fieldData);
  let patientDOB = formData.veteranInformation?.veteranDob;
  if (!isClaimantVeteran(formData)) {
    patientDOB = formData?.claimantInformation?.claimantDob;
  }
  if (dateToValidate && patientDOB) {
    const birthDate = convertToDateField(patientDOB);
    if (!isValidDateRange(birthDate, dateToValidate)) {
      errors.addError(`Please enter a date after the patient's date of birth`);
    }
  }
};

const admissionDateUI = currentOrPastDateUI({
  title: 'When was the claimant admitted to the hospital?',
  dataDogHidden: true,
});

/**
 * uiSchema for Hospitalization Date page
 * Collects the date of hospital admission
 */
export const hospitalizationDateUiSchema = {
  hospitalizationDate: {
    admissionDate: {
      ...admissionDateUI,
      'ui:validations': [...admissionDateUI['ui:validations'], isDateAfterDOB],
    },
  },
  'ui:options': {
    updateUiSchema: (formData, fullData) => {
      const admissionDateLabel = getHospitalizationDateTitle(
        fullData || formData,
      );

      return {
        hospitalizationDate: {
          'ui:options': {
            classNames: 'dd-privacy-mask',
          },
          admissionDate: {
            'ui:title': admissionDateLabel,
          },
        },
      };
    },
  },
};

/**
 * JSON Schema for Hospitalization Date page
 * Validates the admission date field
 */
export const hospitalizationDateSchema = {
  type: 'object',
  required: ['hospitalizationDate'],
  properties: {
    hospitalizationDate: {
      type: 'object',
      required: ['admissionDate'],
      properties: {
        admissionDate: currentOrPastDateSchema,
      },
    },
  },
};
